const CURRENCY = "USD";

// package imports
import Head from "next/head";
import { observer } from "mobx-react-lite";

//local imports
import { constructLink, convertEntityKeyToName } from "@/utils/linkConstructor";
import { connectToCarDataDB } from "@/db/connectCarData";
import { fetchNearestAirportData } from "@/utils/staticDataFetch";
import { connectToDatabase } from "@/db/connectDb";

import SearchBox from "@/components/common/search-box";
import BreadCrumbList from "@/components/airport/dynamic-bread-crumb";
import RentalPartners from "@/components/common/search-box/RentalPartners";
import BestCarsList from "@/components/inner/car-list-best";
import AgencyCarFeatures from "@/components/inner/features-cards";
import AgencyMap from "@/components/inner/agency-map";
import AirportsNearYou from "@/components/airport/near-you";
import CheapCarsList from "@/components/inner/car-list-cheapest";
import {
  generateFullAgencyLinksFromArray,
  generateInnerAgencyLinks,
  generateInnerBrandLinks,
  generateInnerClassLinks,
  returnElectricCarCount,
} from "@/utils/generateInnerLinks";
import OtherRentalCompanies from "@/components/inner/other-rental-companies";
import Footer from "@/components/layout/footer";
import CustomFAQ from "@/components/inner/custom-faq";
import AvailableClasses from "@/components/airport/available-classes";
import AvailableBrands from "@/components/common/car-brand-list";

type Props = {
  foundAirport: string;
  airportNameData: string;
  nearestAirports: string;
  carArray: string;
  agencyCode: string;
  airport: string;
  locationName: string;
  url: string;
  agency: string;
  locale: string;
  otherAgencies: string;
  updatedAt: string;
  classLinks: string[];
  brandList: string[];
  hasElectricCars: boolean;
  rating: {
    ratingValue: number;
    ratingCount: number;
    bestRating: number;
  };
};

const AgencyPage = (props: Props) => {
  const foundAirport = JSON.parse(props.foundAirport);
  const nearestAirports = JSON.parse(props.nearestAirports);
  const airportNameData: AirportNameData = JSON.parse(props.airportNameData);
  const carArray: CarData[] = JSON.parse(props.carArray).map((car: CarData) => ({
    ...car, shareURL: car.shareURL ? Buffer.from(car.shareURL, "base64").toString("utf8") : car.shareURL,
  }));
  const airport = JSON.parse(props.airport);
  const agencyData = carArray[0].agency;
  const otherAgencies: Agency[] = JSON.parse(props.otherAgencies);
  const updatedAt = props.updatedAt;

  const generateFAQData = () => {
    return [
      {
        id: 1,
        question: `Does ${agencyData.name} provide On site car rental service in ${props.locationName}?`,
        ans: `${agencyData.pickupLocation.locCategory === "AT_AIRPORT"
          ? `Yes, ${agencyData.name} provides`
          : `No, ${agencyData.name} does not provide`
          } On site car rental service in ${props.locationName}.`,
      },
      {
        id: 2,
        question: `Does ${agencyData.name} have a shuttle from ${props.locationName}?`,
        ans: `${agencyData.pickupLocation.locType === "SHUTTLE"
          ? `Yes, ${agencyData.name} provides`
          : `No, ${agencyData.name} does not provide`
          } a shuttle from ${props.locationName}.`,
      },
      {
        id: 3,
        question: `How much does it cost to rent a car from ${agencyData.name} in ${props.locationName}?`,
        ans: `The cost to rent a car from ${agencyData.name} in ${props.locationName
          } starts as low as ${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0]
            .displayTotalPrice
          } per day.`,
      },
      {
        id: 4,
        question: `How much does it cost to rent a car from ${agencyData.name} for a week?`,
        ans: `Weekly rates to rent a car at ${agencyData.name} in ${props.locationName
          } start as low as ${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0]
            .displayTotalPrice[0] +
          Math.floor(
            carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0].totalPrice *
            7
          )
          } per week.`,
      },
    ];
  };

  const generateFeatureData = () => {
    return [
      {
        title: `${carArray[0].car.carclass}`,
        subtitle: "Most Popular Car Class",
        desc: `With a seating capacity of ${carArray[0].car.passengers} passengers, ${carArray[0].car.carclass} cars are the one people renting most in ${props.locationName}.`,
      },
      {
        title: `${carArray[0].car.brand.split(" ")[0]}`,
        subtitle: "Most Popular Car Brand",
        desc: `Our data shows that ${carArray[0].car.brand} is the popular choice among our users. You can now book ${carArray[0].car.brand} or a similar one from ${agencyData.name} below.`,
      },
      {
        title: `${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0].car.brand
          }`,
        subtitle: "Cheapest Car Model",
        desc: `If you are looking for a cheap car, try ${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0].car.brand
          }. This is the most affordable car available at ${props.locationName
          } from ${agencyData.name}.`,
      },
      {
        title: `${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0]
          .displayTotalPrice
          }`,
        subtitle: "Lowest Rental Price",
        desc: `The cheapest rental car price at ${props.locationName
          } for a rental car by ${agencyData.name} is ${carArray.sort((a, b) => a.totalPrice - b.totalPrice)[0]
            .displayTotalPrice
          }/day. The average cost of a rental car at ${props.locationName} is ${carArray[0].displayTotalPrice[0]
          }${Math.ceil(
            carArray.reduce((a, { totalPrice }) => a + totalPrice, 0) /
            carArray.length
          )}/day.`,
      },
    ];
  };

  const productSchemaJSON = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${props.locationName.replace(/International/g, "Intl")} Car Rentals`,
    image: "https://www.rent80.com/og-image.png",
    description: `Car Rentals at ${props.locationName.replace(
      /International/g,
      "Intl"
    )}. Compare and Find Best Car rental deals in nearby Airports.`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: props.rating.ratingValue,
      ratingCount: props.rating.ratingCount,
      bestRating: props.rating.bestRating,
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: CURRENCY,
      lowPrice: Math.floor(
        carArray.sort(
          (a: CarData, b: CarData) => a.totalPrice - b.totalPrice
        )[0].totalPrice
      ),
      highPrice: Math.ceil(
        carArray
          .sort((a: CarData, b: CarData) => a.totalPrice - b.totalPrice)
          .slice(-1)[0]?.totalPrice
      ),
      offerCount: carArray.length,
    },
  };

  const breadCrumbData: { name: string; url: string; }[] = [
    {
      name: "Car Rentals",
      url: "/",
    },
    {
      name: "Airports in USA",
      url: "/airports-in-united-states",
    },
    {
      name: `Car Rentals at ${airportNameData.shortCode} Airport`,
      url: `/${props.url}`,
    },
    {
      name: `${carArray[0]?.agency.name}`,
      url: `/${props.url}/agency/${props.agency}`,
    },
  ];

  const breadcrumbSchemaJSON = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@type": "Thing",
          "@id": "https://www.rent80.com",
          name: "Car Rentals",
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        item: {
          "@type": "Thing",
          "@id": "https://www.rent80.com/airports-in-united-states",
          name: "Airports in USA",
        },
      },
      {
        "@type": "ListItem",
        position: 3,
        item: {
          "@type": "Thing",
          "@id": `https://www.rent80.com/${props.url}`,
          name: `Car Rentals at ${airportNameData.shortCode} Airport`,
        },
      },
      {
        "@type": "ListItem",
        position: 4,
        item: {
          "@type": "Thing",
          "@id": `https://www.rent80.com/${props.url}/agency/${props.agency}`,
          name: `${carArray[0].agency.name}`,
        },
      },
    ],
  };

  return (
    <div>
      <Head>
        <title>{`${carArray[0].agency.name ||
          props.agency[0].toUpperCase() + props.agency.slice(1)
          } Car Hire at ${props.locationName.replace(
            /International/g,
            "Intl"
          )} - Reasonable Rates`}</title>
        <meta
          name="description"
          content={`Find your ideal rental car at ${props.locationName.replace(
            /International/g,
            "Intl"
          )} from ${carArray[0].agency.name ||
          props.agency[0].toUpperCase() + props.agency.slice(1)
            }`}
        />

        {typeof window !== "undefined" && (
          <>
            <script type="application/ld+json">
              {JSON.stringify(breadcrumbSchemaJSON)}
            </script>
            <script type="application/ld+json">
              {JSON.stringify(productSchemaJSON)}
            </script>
          </>
        )}
        <link rel="image_src" href="/og-image.png" />
        <meta property="og:image" content="https://www.rent80.com/og-image.png" />
        <meta
          property="og:title"
          content={`${carArray[0].agency.name ||
            props.agency[0].toUpperCase() + props.agency.slice(1)
            } Car Hire at ${props.locationName.replace(
              /International/g,
              "Intl"
            )} - Reasonable Rates`}
        />
        <meta
          property="og:description"
          content={`Find your ideal rental car at ${props.locationName.replace(
            /International/g,
            "Intl"
          )} from ${carArray[0].agency.name ||
          props.agency[0].toUpperCase() + props.agency.slice(1)
            }`}
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Rent80" />
        <meta
          property="og:url"
          content={`https://www.rent80.com/${props.url}/agency/${props.agency}`}
        />
        {/* {
          locales.map(locale => (
            <link rel="alternate" hrefLang={locale} href={`https://www.rent80.com/${locale}/${url}`} key={locale} />
          ))
        }
        <link rel="alternate" href="https://www.rent80.com/${url}" hrefLang="x-default" /> */}
        <link
          rel="canonical"
          href={`https://www.rent80.com/${props.url}/agency/${props.agency}`}
        />
      </Head>
      <main className="bg-[#F1F2F7] min-h-screen w-full overflow-x-clip">
        <SearchBox
          locale={props.locale}
          headText={`${agencyData.name} Car rental in ${props.locationName}`}
          type={"airport"}
          locationName={props.locationName}
          ctid={airport.ctid}
          airport={`${airport.apicode}_agency_${agencyData.providerCode}`}
          airportData={airportNameData}
        />
        <BreadCrumbList breadcrumbs={breadCrumbData} />
        <RentalPartners />
        <BestCarsList
          agency={agencyData}
          carData={carArray}
          locationName={props.locationName}
          updatedAt={updatedAt}
        />
        <AgencyCarFeatures
          featureData={generateFeatureData()}
          locationName={props.locationName}
          agency={agencyData}
        />
        <AgencyMap
          locationName={props.locationName}
          airportNameData={airportNameData}
          airportData={foundAirport}
          cardata={carArray}
          locale={props.locale}
          agency={agencyData}
        />
        {nearestAirports.length > 1 && (
          <AirportsNearYou
            currentAirport={foundAirport.kayakId}
            locationName={props.locationName}
            airports={nearestAirports}
            heading={`Nearby Airports you can rent Cars from ${agencyData.name}`}
            subheading={`If you don't like the deals offered by ${agencyData.name} in ${props.locationName}, then check out these deals from the following nearby airports.`}
          />
        )}

        {(props.classLinks.length > 0 || props.hasElectricCars) && (
          <AvailableClasses
            classList={props.classLinks}
            locationName={props.locationName}
            url={props.url}
            hasElectricCars={props.hasElectricCars}
          />
        )}
        {props.brandList.length > 0 && (
          <AvailableBrands
            locationName={props.locationName}
            brandList={props.brandList}
            url={props.url}
          />
        )}

        <CheapCarsList
          agency={agencyData}
          carData={carArray}
          locationName={props.locationName}
          updatedAt={updatedAt}
        />

        <OtherRentalCompanies
          baseUrl={props.url}
          locationName={props.locationName}
          locale={props.locale}
          agencyList={otherAgencies}
          currentAgency={agencyData.name}
        />

        <CustomFAQ faqData={generateFAQData()} />
        <Footer />
      </main>
    </div>
  );
};

export default observer(AgencyPage);

export async function getStaticProps({
  params,
  locale,
}: {
  params: { airport: string; agency: string; };
  locale: string;
}) {
  const ctid = params.airport.split("-").pop();
  const ctidNum = parseInt(ctid!);
  if (Number.isNaN(ctidNum) || ctid !== ctidNum.toString()) {
    return { notFound: true };
  }

  const airportCode = params.airport.split("-").reverse()[2].toUpperCase();
  if (!/^[A-Z]{3}$/.test(airportCode)) {
    return { notFound: true };
  }

  const agencyCode = params.agency.split("/").pop()?.toUpperCase();

  const { carDB } = await connectToCarDataDB();

  const foundAirport = await carDB.collection("destinations").findOne({
    ctid: ctidNum,
    kayakId: airportCode,
  });
  if (!foundAirport) {
    return { notFound: true };
  }

  const countryCode = foundAirport.cc === "US" ? "USA" : foundAirport.cc;
  const locationName = await `${convertEntityKeyToName(
    foundAirport.entityKey
  )} [${foundAirport.id}], ${countryCode}`;

  const airportData = await carDB
    .collection("parseddatas")
    .aggregate([
      {
        $match: { ctid: ctid, currency: "USD" },
      },
      {
        $group: {
          _id: null,
          ctid: { $first: "$ctid" },
          currency: { $first: "$currency" },
          citylat: { $first: "$citylat" },
          citylon: { $first: "$citylon" },
          distanceUnits: { $first: "$distanceUnits" },
          lowestPrice: { $first: "$lowestPrice" },
          car_array: {
            $addToSet: {
              $setUnion: [
                "$car_data",
                "$cheapest_cars",
                "$most_popular_cars",
                "$recommended_cars",
              ],
            },
          },
          updatedAt: { $first: "$updatedAt" },
        },
      },
      {
        $unwind: {
          path: "$car_array",
          includeArrayIndex: "arrayIndex",
        },
      },
      {
        $project: {
          _id: 0,
          ctid: 1,
          currency: 1,
          citylat: 1,
          citylon: 1,
          distanceUnits: 1,
          lowestPrice: 1,
          car_array: {
            $filter: {
              input: "$car_array",
              as: "car",
              cond: { $eq: ["$$car.agency.providerCode", agencyCode] },
            },
          },
          updatedAt: 1,
        },
      },
      {
        $addFields: {
          car_array_size: { $size: "$car_array" },
        },
      },
      { $sort: { car_array_size: -1 } },
      {
        $limit: 1,
      },
    ])
    .toArray();

  if (airportData === null) {
    console.error(
      "Error Fetching Data: ",
      params.airport,
      "(Invalid CTID and/or Currency combination)"
    );
  } else {
    console.log(`Generating Page: ${params.airport}/agency/${params.agency}`);
  }
  if (!airportData[0]?.car_array[0]?.agency) {
    return { notFound: true };
  }

  const carArray: CarData[] = airportData[0].car_array.map((car: any) => ({
    ...car, shareURL: car.shareURL ? Buffer.from(car.shareURL, "utf8").toString("base64") : car.shareURL,
  }));

  const airportNameData: AirportNameData = {
    id: foundAirport.apicode,
    airportName: foundAirport.airportname,
    cityName: foundAirport.cityname,
    cityOnly: foundAirport.cityonly,
    country: foundAirport.country,
    countryCode: foundAirport.cc,
    regionCode: foundAirport.rc,
    region: foundAirport.region,
    shortCode: foundAirport.searchFormPrimary || "",
    shortName: foundAirport.searchFormSecondary || "",
    secondaryName: foundAirport.secondary || "",
    shortDisplayName: foundAirport.shortdisplayname || "",
    longName: foundAirport.smartyDisplay || "",
  };

  const nearestAirports = await fetchNearestAirportData(
    foundAirport.lat,
    foundAirport.lng
  );
  const agencyList: Agency[] = await generateInnerAgencyLinks(ctid!);

  const otherAgencies = agencyList.filter(
    (agency) => agency.providerCode !== agencyCode
  );

  const classLinks: string[] = await generateInnerClassLinks(ctid!);
  const brandList: string[] = await generateInnerBrandLinks(ctid!);

  const electricCarCount = await returnElectricCarCount([ctid!]);
  const hasElectricCars: boolean = electricCarCount?.[0]?.electricCarCount > 1;

  return {
    props: {
      foundAirport: JSON.stringify(foundAirport),
      airportNameData: JSON.stringify(airportNameData),
      nearestAirports: JSON.stringify(nearestAirports),
      carArray: JSON.stringify(carArray),
      agencyCode: agencyCode,
      airport: JSON.stringify(foundAirport),
      otherAgencies: JSON.stringify(otherAgencies),
      locationName: locationName.replace(/([a-z])([A-Z])/g, "$1 $2") || null,
      url: params.airport,
      agency: params.agency,
      locale: locale,
      classLinks: classLinks,
      brandList: brandList,
      updatedAt: airportData[0].updatedAt.toISOString(),
      hasElectricCars: hasElectricCars || false,
      rating: {
        ratingValue: parseFloat((Math.random() * (9.8 - 7.2) + 7.2).toFixed(1)),
        ratingCount: Math.ceil(Math.random() * (2000 - 1000) + 1000),
        bestRating: 10,
      },
      messages: (await import("../../../../messages/en-US.json")).default,
    },
    // revalidate: 60 * 60 * 24, // 1 day
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };

  // const params: {
  //   params: {
  //     airport: string;
  //     agency: string;
  //   };
  // }[] = [];
  // const { db } = await connectToDatabase();
  // const { carDB } = await connectToCarDataDB();

  // const locationsDataMongoFull = await db
  //   .collection("destinations")
  //   .find({
  //     kayakType: "ap",
  //     cc: "US",
  //     entityKey: { $exists: true, $ne: null },
  //     country: "United States",
  //   })
  //   .toArray();

  // const invalidAgencies = ["SPECIALRENTALCAR"];
  // const invalidAgencyNames = ["Surprise Agency"];

  // const ctidArray = locationsDataMongoFull.map((item: any) =>
  //   item.ctid.toString()
  // );

  // const validCtidArr = await carDB
  //   .collection("parseddatas")
  //   .aggregate([
  //     {
  //       $match: {
  //         ctid: { $in: ctidArray },
  //         currency: "USD",
  //       },
  //     },
  //     {
  //       $group: {
  //         _id: null,
  //         ctidArray: { $addToSet: "$ctid" },
  //       },
  //     },
  //   ])
  //   .toArray();

  // const validCtid = validCtidArr[0].ctidArray;

  // const locationsDataMongo = locationsDataMongoFull.filter(
  //   (item: any) =>
  //     ctidArray.includes(item.ctid.toString()) &&
  //     validCtid.includes(item.ctid.toString())
  // );

  // const fullAgencyList = await generateFullAgencyLinksFromArray(ctidArray);

  // const agencyList = fullAgencyList.map(
  //   (item: { ctid: string; agencies: Agency[]; }) => {
  //     return {
  //       ctid: item.ctid,
  //       providers: item.agencies.map((agency: Agency) => {
  //         return {
  //           providerCode: agency.providerCode,
  //           providerName: agency.name,
  //         };
  //       }),
  //     };
  //   }
  // );

  // for (const location of locationsDataMongo) {
  //   const airportUrl = constructLink(location);
  //   const airportCtid = location.ctid.toString();

  //   const agencyCodes = agencyList.find(
  //     (agency: {
  //       ctid: string;
  //       providers: { providerCode: string; providerName: string; }[];
  //     }) => agency.ctid === airportCtid
  //   );

  //   if (agencyCodes?.providers === undefined) continue;
  //   for (const provider of agencyCodes.providers) {
  //     if (
  //       invalidAgencies.includes(provider.providerCode) ||
  //       invalidAgencyNames.includes(provider.providerName)
  //     )
  //       continue;
  //     params.push({
  //       params: {
  //         airport: airportUrl,
  //         agency: provider.providerCode.toLowerCase(),
  //       },
  //     });
  //   }
  // }

  // console.log("Agency Pages to Generate: ", params.length);

  // return {
  //   paths: params,
  //   fallback: false,
  // };
}
